import { useCallback, useEffect, useRef, useState } from 'react';
import type { GeoCoords, GeoStatus } from '../types/feed';

const TIMEOUT_MS = 3000;
const MAX_AGE_MS = 60_000;

export function useGeoLocation(autoRequest = true) {
  const [status, setStatus] = useState<GeoStatus>('pending');
  const [coords, setCoords] = useState<GeoCoords>(null);
  const requestedRef = useRef(false);

  const request = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }
    requestedRef.current = true;
    setStatus('pending');
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        setStatus('timeout');
      }
    }, TIMEOUT_MS + 200);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus('granted');
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'timeout');
      },
      { timeout: TIMEOUT_MS, maximumAge: MAX_AGE_MS, enableHighAccuracy: false }
    );
  }, []);

  useEffect(() => {
    if (autoRequest && !requestedRef.current) request();
  }, [autoRequest, request]);

  return { status, coords, request };
}
